import { Graphics, Text, _ReactPixi } from '@inlet/react-pixi';
import gsap from 'gsap';
import { TextStyle } from 'pixi.js';
import {
  Dispatch,
  FC,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as PIXI from 'pixi.js';
import { PADDING } from 'custom-constants';
import { Bubble } from 'domain/bubble';
import { CustomPixiContainer } from '../CustomPixiContainer';

interface Props {
  text: string;
  width: number;
  style: TextStyle;
  x: number;
  y: number;
  onNext: () => void;
  setAnswersSize: Dispatch<SetStateAction<number[]>>;
  p?: number;
  visible?: boolean;
  currentBubble?: Bubble;
}

export const AnswerComponent: FC<Props> = memo(
  ({ text, width, style, x, y, onNext, setAnswersSize, p = 0, visible, currentBubble }) => {
    const textRef = useRef<_ReactPixi.IText>(null);
    const [textHeight, setTextHeight] = useState(0);
    const [textWidth, setTextWidth] = useState(0);

    useEffect(() => {
      if (textHeight) {
        setAnswersSize((prev) => [...prev, textHeight + p]);
      }
    }, [textHeight, currentBubble]);

    const borderRef = useRef(null);

    useEffect(() => {
      if (visible) {
        gsap.to(borderRef.current, {
          alpha: 0,
          duration: 0,
        });
        gsap.to(borderRef.current, {
          alpha: 1,
        });
      }
    }, [visible]);

    const handlePointerDownNext = () => {
      const ev = new CustomEvent('storyClick');
      window.dispatchEvent(ev);
      onNext();
    };

    const drawAnswerBorder = useCallback(
      (g: PIXI.Graphics) => {
        g.lineStyle(2, 0x474747, 1);
        g.beginFill(0x353535, 0.8);
        g.drawRoundedRect(0, 0, width + PADDING * 2, textHeight + PADDING * 2, 15);
        g.endFill();
        g.lineStyle(0);
      },
      [width, textHeight]
    );

    return (
      <CustomPixiContainer
        width={width}
        height={textHeight + p}
        x={x}
        y={y}
        interactive
        buttonMode
        pointerdown={handlePointerDownNext}
        visible={visible}
        forwardRef={borderRef}
      >
        <Graphics draw={drawAnswerBorder} />
        <Text
          text={text}
          x={PADDING}
          y={PADDING}
          style={style}
          width={Math.min(width, textWidth)}
          ref={(e) => {
            setTextHeight(Math.floor(e?.texture.orig.height || 0));
            setTextWidth(Math.floor(e?.texture.orig.width || 0));
            return textRef;
          }}
        />
      </CustomPixiContainer>
    );
  }
);
